/**
 * Le contrat de la supervision des tontines, côté navigateur (lot 5 de
 * `docs/PLAN_TONTINE.md`, `EX-TON-014`).
 *
 * **Ce fichier ne décide de rien.** Les types suivent
 * `TontineSupervisionMessages.java`, champ pour champ, et un contrôle les y
 * confronte (`test/app/tontines.test.tsx`).
 *
 * ## Deux lectures, une seule écriture — et pas ici
 *
 * La liste et la fiche sont servies par `TontinesApi`, sous `TONTINE_VIEW`, avec
 * un motif inscrit au journal. La **dissolution** ne passe pas par une route
 * des tontines : elle part sur la route unique des actions, sous
 * `TONTINE_DISSOLUTION`, que la matrice pose en double validation. Le socle
 * rend alors `202` avec l'identifiant d'une demande, et **rien n'a bougé** :
 * aucun membre n'a encore récupéré un franc. L'écran doit le dire ainsi.
 *
 * ## Les montants sont en unités mineures, et un nul n'est pas zéro
 *
 * Un tour non versé n'a ni pot, ni frais, ni net : ce n'est pas « 0 F ». Un
 * sous-compte introuvable au grand livre n'a pas de solde : ce n'est pas « 0 F »
 * non plus — c'est une anomalie, que le serveur compte déjà comme un écart.
 */

import type { ApiClient } from './api-client.js';

/** Le chemin de la supervision. */
export const tontinesPath = '/api/backoffice/tontines';

/** Le chemin de la route unique des actions habilitables. */
export const tontineActionsPath = '/api/backoffice/actions';

/** Le code de l'action de dissolution, à deux signatures (V102). */
export const tontineDissolutionAction = 'TONTINE_DISSOLUTION';

/** Le motif écrit au journal pour les lectures de la supervision. */
export const tontinesReason = 'supervision-des-tontines';

/** Le filtre qui ne garde que les tontines dont un tour est reporté. */
export const postponedFilter = 'REPORTE';

/** Une ligne de la liste. Suit `TontineRowView`. */
export interface TontineRow {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly organizerName: string | null;
  readonly organizerMsisdn: string | null;
  readonly contributionAmount: number;
  readonly period: string;
  readonly memberCount: number;
  readonly maxMembers: number;
  readonly currentRoundNumber: number | null;
  readonly currentRoundStatus: string | null;
  readonly currentRoundDueOn: string | null;
  readonly hasPostponedRound: boolean;
  readonly ledgerBalance: number | null;
  readonly expectedBalance: number;
  readonly balanceMismatch: boolean;
  readonly dissolvable: boolean;
  readonly createdAt: string | null;
  readonly startedAt: string | null;
  readonly suspendedAt: string | null;
  readonly endedAt: string | null;
}

/** Une page de la liste. Suit `TontinePageView`. */
export interface TontinePage {
  readonly tontines: readonly TontineRow[];
  readonly page: number;
  readonly pageSize: number;
  readonly total: number;
  readonly suspendedCount: number;
  readonly postponedCount: number;
  readonly mismatchCount: number;
}

/** Un membre, identité complète. Suit `MemberView`. */
export interface TontineMember {
  readonly memberId: string;
  readonly customerId: string;
  readonly publicRef: string | null;
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly msisdn: string | null;
  readonly displayName: string;
  readonly joinOrder: number;
  readonly position: number | null;
  readonly status: string;
  readonly depositStatus: string;
  readonly organizer: boolean;
  readonly joinedAt: string | null;
}

/** Un tour. Suit `RoundView`. */
export interface TontineRound {
  readonly id: string;
  readonly number: number;
  readonly dueOn: string;
  readonly status: string;
  readonly beneficiaryMemberId: string;
  readonly beneficiaryName: string | null;
  readonly paidOutAt: string | null;
  readonly payoutAmount: number | null;
  readonly payoutFee: number | null;
  readonly netAmount: number | null;
  readonly payoutTransactionId: string | null;
  readonly postponedAt: string | null;
  readonly contributionCount: number;
}

/** Une cotisation du tour en cours. Suit `ContributionView`. */
export interface TontineContribution {
  readonly memberId: string;
  readonly memberName: string | null;
  readonly amount: number;
  readonly source: string;
  readonly transactionId: string | null;
  readonly paidAt: string | null;
  readonly refundedAt: string | null;
  readonly refundTransactionId: string | null;
}

/** Le tour dont les cotisations sont (ou étaient) sur le compte. Suit `CurrentRoundView`. */
export interface TontineCurrentRound {
  readonly number: number;
  readonly status: string;
  readonly dueOn: string;
  readonly contributions: readonly TontineContribution[];
  readonly expectedCount: number;
}

/** Un mouvement de caution. Suit `DepositMovementView`. */
export interface TontineDepositMovement {
  readonly memberId: string;
  readonly memberName: string | null;
  readonly kind: string;
  readonly amount: number;
  readonly transactionId: string | null;
  readonly roundNumber: number | null;
  readonly occurredAt: string | null;
}

/** Le solde au grand livre et l'invariant d'AD-30. Suit `BalanceView`. */
export interface TontineBalance {
  readonly accountNumber: string | null;
  readonly ledgerBalance: number | null;
  readonly heldDeposits: number;
  readonly heldDepositCount: number;
  readonly pendingContributions: number;
  readonly pendingContributionCount: number;
  readonly expected: number;
  readonly gap: number;
  readonly consistent: boolean;
}

/** La fiche d'une tontine. Suit `TontineDetailView`. */
export interface TontineDetail {
  readonly id: string;
  readonly name: string;
  readonly status: string;
  readonly inviteCode: string;
  readonly drawMode: string;
  readonly seedDigest: string | null;
  readonly seed: string | null;
  readonly contributionAmount: number;
  readonly period: string;
  readonly maxMembers: number;
  readonly firstDueOn: string | null;
  readonly createdAt: string | null;
  readonly startedAt: string | null;
  readonly suspendedAt: string | null;
  readonly endedAt: string | null;
  readonly dissolutionRef: string | null;
  readonly organizer: TontineMember | null;
  readonly members: readonly TontineMember[];
  readonly rounds: readonly TontineRound[];
  readonly currentRound: TontineCurrentRound | null;
  readonly depositMovements: readonly TontineDepositMovement[];
  readonly balance: TontineBalance;
  readonly dissolvable: boolean;
}

/** Ce que rend la route des actions. */
export interface TontineActionOutcome {
  readonly outcome: string;
  readonly approvalRequestId: string | null;
  readonly detail: string | null;
}

/**
 * Lit une page de tontines.
 *
 * @param filter un état de tontine, `REPORTE`, ou `null` pour toutes
 * @param page   à partir de 1
 */
export async function fetchTontines(
  client: ApiClient,
  filter: string | null,
  page: number,
  reason: string = tontinesReason,
): Promise<TontinePage> {
  const parametres = new URLSearchParams({ reason, page: String(page) });
  if (filter !== null) parametres.set('status', filter);
  const recue = await client.request<TontinePage>(`${tontinesPath}?${parametres.toString()}`);
  // **Une réponse de travers ne doit pas emporter l'écran.** Sans cette garde,
  // `.map` sur un objet qui n'est pas une liste lèverait jusqu'à la racine React.
  if (!Array.isArray(recue?.tontines)) {
    throw new Error('ÉCHEC : la réponse qui devait porter les tontines n’est pas une page.');
  }
  return recue;
}

/** Lit la fiche d'une tontine. */
export async function fetchTontine(
  client: ApiClient,
  id: string,
  reason: string = tontinesReason,
): Promise<TontineDetail> {
  const parametres = new URLSearchParams({ reason });
  const recue = await client.request<TontineDetail>(
    `${tontinesPath}/${encodeURIComponent(id)}?${parametres.toString()}`,
  );
  if (!Array.isArray(recue?.members) || !Array.isArray(recue?.rounds)) {
    throw new Error('ÉCHEC : la réponse qui devait porter une tontine n’en est pas une.');
  }
  return recue;
}

/**
 * Demande la dissolution d'une tontine démarrée.
 *
 * **En double validation.** Le socle rend `202` et l'identifiant de la demande :
 * la tontine n'est PAS dissoute, et aucun membre n'a rien récupéré. C'est la
 * seconde signature, sur la file de double validation, qui exécute.
 */
export async function requestTontineDissolution(
  client: ApiClient,
  demande: { readonly tontineId: string; readonly reason: string },
): Promise<TontineActionOutcome> {
  return client.request<TontineActionOutcome>(
    `${tontineActionsPath}/${tontineDissolutionAction}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entityRef: demande.tontineId, reason: demande.reason, payload: {} }),
    },
  );
}
